import { ContentType, NotificationItemType } from '@atlassian/learning-components';

import { getCourseUrl, getLessonUrl, getPathUrl } from 'src/utils/getUrl';
import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { ContentfulLearningPath } from 'src/graphql/queries/GetLearningPath';
import { NotificationItem } from 'src/services/http/notifications';

export const sortNotificationsByDate = (notifications?: NotificationItem[]): NotificationItem[] => {
  if (!notifications) {
    return [];
  }

  return notifications.sort(
    (firstNotification, secondNotification) =>
      new Date(secondNotification.publishedAt).getTime() - new Date(firstNotification.publishedAt).getTime(),
  );
};

export const mapLearningPathNotificationsData = (
  notifications: NotificationItem[],
  learningPath: ContentfulLearningPath,
): NotificationItemType[] => {
  const learningPathsNotifications = notifications.filter(
    (notification) => notification.contentfulType === ContentType.LEARNING_PATH,
  );
  const coursesNotifications = notifications.filter(
    (notification) => notification.contentfulType === ContentType.COURSE,
  );
  const lessonsNotifications = notifications.filter(
    (notification) => notification.contentfulType === ContentType.LESSON,
  );

  const mappedLearningPathsNotifications = learningPathsNotifications.map((notification) => ({
    id: notification.id,
    title: notification.title,
    description: notification.description,
    resourceLink: `/learning${getPathUrl(notification.slug)}`,
    date: formatDate(notification.publishedAt),
    contentType: notification.contentfulType,
  }));

  const mappedCoursesNotifications = coursesNotifications.map((notification) => ({
    id: notification.id,
    title: notification.title,
    description: notification.description,
    resourceLink: `/learning${getCourseUrl(notification.slug, learningPath.slug)}`,
    date: formatDate(notification.publishedAt),
    contentType: notification.contentfulType,
  }));

  const mappedLessonsNotifications = lessonsNotifications.map((notification) => {
    const course = learningPath.coursesCollection.items.find((course) =>
      course.lessonsCollection.items.find((lesson) => lesson.slug === notification.slug),
    );

    return {
      id: notification.id,
      title: notification.title,
      description: notification.description,
      resourceLink: `/learning${getLessonUrl({
        lessonSlug: notification.slug,
        courseSlug: course?.slug,
        pathSlug: learningPath.slug,
      })}`,
      date: formatDate(notification.publishedAt),
      contentType: notification.contentfulType,
    };
  });

  return [...mappedLessonsNotifications, ...mappedCoursesNotifications, ...mappedLearningPathsNotifications];
};

export const mapCourseNotificationsData = (
  notifications: NotificationItem[],
  course: ContentfulSmCourse,
): NotificationItemType[] => {
  const coursesNotifications = notifications.filter(
    (notification) => notification.contentfulType === ContentType.COURSE,
  );
  const lessonsNotifications = notifications.filter(
    (notification) => notification.contentfulType === ContentType.LESSON,
  );

  const mappedCoursesNotifications = coursesNotifications.map((notification) => ({
    id: notification.id,
    title: notification.title,
    description: notification.description,
    resourceLink: `/learning${getCourseUrl(notification.slug)}`,
    date: formatDate(notification.publishedAt),
    contentType: notification.contentfulType,
  }));

  const mappedLessonsNotifications = lessonsNotifications.map((notification) => ({
    id: notification.id,
    title: notification.title,
    description: notification.description,
    resourceLink: `/learning${getLessonUrl({ lessonSlug: notification.slug, courseSlug: course?.slug })}`,
    date: formatDate(notification.publishedAt),
    contentType: notification.contentfulType,
  }));

  return [...mappedLessonsNotifications, ...mappedCoursesNotifications];
};

export const mapLessonNotificationsData = (notifications: NotificationItem[]): NotificationItemType[] => {
  const lessonsNotifications = notifications.filter(
    (notification) => notification.contentfulType === ContentType.LESSON,
  );

  return lessonsNotifications.map((notification) => ({
    id: notification.id,
    title: notification.title,
    description: notification.description,
    resourceLink: `/learning${getLessonUrl({ lessonSlug: notification.slug })}`,
    date: formatDate(notification.publishedAt),
    contentType: notification.contentfulType,
  }));
};

const formatDate = (data: string): string => {
  const date = new Date(data);
  const day = date.getDate();
  const year = date.getFullYear();
  const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
  const suffix = getOrdinalSuffix(day);

  return `${month} ${day}${suffix}, ${year}`;
};

const getOrdinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) {
    return 'th';
  }

  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};
